import { supabase } from './supabase';
import {
  ChatAttachmentPreview,
  ChatAttachmentType,
  ChatMessage,
  ConversationSummary,
  ShareableItem,
} from '../types/chat';

export const MESSAGE_MAX_LENGTH = 2000;

/** Fired after messages are marked read so the nav badge can recount. */
export const DM_READ_EVENT = 'dhh:dm-read';

/** Control characters that carry no meaning in a chat message. */
// Built from code points so the source has no control-character regex (eslint no-control-regex).
const CONTROL_CHARS = new RegExp(
  `[${String.fromCharCode(0)}-${String.fromCharCode(8)}${String.fromCharCode(11)}${String.fromCharCode(12)}${String.fromCharCode(14)}-${String.fromCharCode(31)}${String.fromCharCode(127)}]`,
  'g'
);
/** Bidi overrides — used to disguise text as something it isn't. */
const BIDI_CHARS = /[\u202A-\u202E\u2066-\u2069]/g;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Chat carries plain text only — strip anything that isn't that. */
export function sanitizeMessageBody(input: string): string {
  return input
    .replace(CONTROL_CHARS, '')
    .replace(BIDI_CHARS, '')
    .trim()
    .slice(0, MESSAGE_MAX_LENGTH);
}

export const chatService = {
  /** Find or create the 1:1 thread with another artist. */
  async getOrCreateConversation(peerId: string): Promise<string> {
    const { data, error } = await supabase.rpc('get_or_create_conversation', {
      peer_id: peerId,
    });
    if (error) throw error;
    return data as string;
  },

  async listConversations(): Promise<ConversationSummary[]> {
    const { data, error } = await supabase.rpc('list_conversations');
    if (error) throw error;
    return ((data as ConversationSummary[] | null) ?? []).map(row => ({
      ...row,
      unread_count: Number(row.unread_count ?? 0),
    }));
  },

  /** Peer profile for a thread (used when the list hasn't loaded it yet). */
  async getConversationPeer(
    conversationId: string,
    currentUserId: string
  ): Promise<{ id: string; username: string; role: string; state: string | null } | null> {
    const { data: conv, error } = await supabase
      .from('conversations')
      .select('id, user_a, user_b')
      .eq('id', conversationId)
      .maybeSingle();

    if (error || !conv) return null;

    const peerId = conv.user_a === currentUserId ? conv.user_b : conv.user_a;
    const { data: peer } = await supabase
      .from('profiles')
      .select('id, username, role, state')
      .eq('id', peerId)
      .maybeSingle();

    return (peer as any) ?? null;
  },

  async getMessages(conversationId: string, limit = 100): Promise<ChatMessage[]> {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    // Oldest first for rendering.
    return ((data as ChatMessage[] | null) ?? []).slice().reverse();
  },

  async sendMessage(params: {
    conversationId: string;
    senderId: string;
    body?: string;
    attachmentType?: ChatAttachmentType | null;
    attachmentId?: string | null;
  }): Promise<ChatMessage> {
    const body = params.body ? sanitizeMessageBody(params.body) || null : null;
    const attachmentType = params.attachmentType ?? null;
    const attachmentId = params.attachmentId ?? null;

    // Only a beat or a verse may ride along with a message — nothing else.
    if ((attachmentType === null) !== (attachmentId === null)) {
      throw new Error('Invalid attachment');
    }
    if (attachmentType && attachmentType !== 'beat' && attachmentType !== 'verse') {
      throw new Error('Only beats and verses can be shared in chat');
    }
    if (attachmentId && !UUID_PATTERN.test(attachmentId)) {
      throw new Error('Invalid attachment');
    }

    if (!body && !attachmentId) {
      throw new Error('Message cannot be empty');
    }

    const { data, error } = await supabase
      .from('messages')
      .insert([
        {
          conversation_id: params.conversationId,
          sender_id: params.senderId,
          body,
          attachment_type: attachmentType,
          attachment_id: attachmentId,
        },
      ])
      .select('*')
      .single();

    if (error) throw error;
    return data as ChatMessage;
  },

  async markRead(conversationId: string): Promise<void> {
    const { error } = await supabase.rpc('mark_conversation_read', {
      conv_id: conversationId,
    });
    if (error) throw error;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(DM_READ_EVENT, { detail: { conversationId } }));
    }
  },

  async getUnreadCount(): Promise<number> {
    const { data, error } = await supabase.rpc('get_unread_message_count');
    if (error) throw error;
    return Number(data ?? 0);
  },

  /** Resolve beat/verse attachments referenced by messages, for preview cards. */
  async resolveAttachments(
    refs: { type: ChatAttachmentType; id: string }[]
  ): Promise<Record<string, ChatAttachmentPreview>> {
    const previews: Record<string, ChatAttachmentPreview> = {};
    if (refs.length === 0) return previews;

    const beatIds = Array.from(new Set(refs.filter(r => r.type === 'beat').map(r => r.id)));
    const verseIds = Array.from(new Set(refs.filter(r => r.type === 'verse').map(r => r.id)));

    if (beatIds.length > 0) {
      const { data } = await supabase
        .from('beats')
        .select('id, title, genre, bpm, producer:profiles!producer_id(username)')
        .in('id', beatIds);

      (data || []).forEach((beat: any) => {
        const producer = Array.isArray(beat.producer) ? beat.producer[0] : beat.producer;
        previews[`beat:${beat.id}`] = {
          id: beat.id,
          type: 'beat',
          title: beat.title,
          subtitle: [producer?.username, beat.genre, beat.bpm ? `${beat.bpm} BPM` : null]
            .filter(Boolean)
            .join(' · '),
          href: `/beats/${beat.id}`,
        };
      });
    }

    if (verseIds.length > 0) {
      const { data } = await supabase
        .from('verses')
        .select('id, beat_id, rapper_username, status, beat:beats(title)')
        .in('id', verseIds);

      (data || []).forEach((verse: any) => {
        // Pending takes must not leak through a shared link in someone else's thread.
        if (verse.status && verse.status !== 'approved') return;
        const beat = Array.isArray(verse.beat) ? verse.beat[0] : verse.beat;
        previews[`verse:${verse.id}`] = {
          id: verse.id,
          type: 'verse',
          title: beat?.title ? `Verse on “${beat.title}”` : 'Verse',
          subtitle: verse.rapper_username ? `by ${verse.rapper_username}` : 'Verse',
          href: verse.beat_id ? `/beats/${verse.beat_id}` : '/home',
        };
      });
    }

    return previews;
  },

  /** The current user's own beats and verses — used by the share picker. */
  async getShareableItems(userId: string): Promise<ShareableItem[]> {
    const [beatsRes, versesRes] = await Promise.all([
      supabase
        .from('beats')
        .select('id, title, genre, bpm')
        .eq('producer_id', userId)
        .order('created_at', { ascending: false })
        .limit(20),
      supabase
        .from('verses')
        .select('id, beat:beats(title)')
        .eq('rapper_id', userId)
        // Unapproved takes are invisible to the other artist, so don't offer them.
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(20),
    ]);

    const beats: ShareableItem[] = (beatsRes.data || []).map((b: any) => ({
      id: b.id,
      type: 'beat' as const,
      title: b.title,
      subtitle: [b.genre, b.bpm ? `${b.bpm} BPM` : null].filter(Boolean).join(' · ') || 'Beat',
    }));

    const verses: ShareableItem[] = (versesRes.data || []).map((v: any) => {
      const beat = Array.isArray(v.beat) ? v.beat[0] : v.beat;
      return {
        id: v.id,
        type: 'verse' as const,
        title: beat?.title ? `Verse on “${beat.title}”` : 'Verse',
        subtitle: 'Your verse',
      };
    });

    return [...beats, ...verses];
  },
};
